import type {
  AccountStatus,
  EditRequestStatus,
  SubmissionStatus,
  TemplateVersion
} from "@/lib/api";

interface StatusPillProps {
  status: AccountStatus | EditRequestStatus | SubmissionStatus | TemplateVersion["status"];
}

const statusConfig = {
  approved: {
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
    dot: "bg-emerald-500",
    label: "Approved"
  },
  pending: {
    className: "border-amber-200 bg-amber-50 text-amber-800",
    dot: "bg-amber-500",
    label: "Pending"
  },
  pending_approval: {
    className: "border-amber-200 bg-amber-50 text-amber-800",
    dot: "bg-amber-500",
    label: "Pending Approval"
  },
  pending_email_verification: {
    className: "border-sky-200 bg-sky-50 text-sky-800",
    dot: "bg-sky-500",
    label: "Email Verification"
  },
  rejected: {
    className: "border-rose-200 bg-rose-50 text-rose-700",
    dot: "bg-rose-500",
    label: "Rejected"
  },
  disabled: {
    className: "border-slate-300 bg-slate-100 text-slate-700",
    dot: "bg-slate-500",
    label: "Disabled"
  },
  draft: {
    className: "border-sky-200 bg-sky-50 text-sky-800",
    dot: "bg-sky-500",
    label: "Draft"
  },
  published: {
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
    dot: "bg-emerald-500",
    label: "Published"
  },
  archived: {
    className: "border-slate-300 bg-slate-100 text-slate-700",
    dot: "bg-slate-500",
    label: "Archived"
  },
  submitted: {
    className: "border-cyan-200 bg-cyan-50 text-cyan-800",
    dot: "bg-cyan-500",
    label: "Submitted"
  },
  superseded: {
    className: "border-slate-200 bg-slate-50 text-slate-700",
    dot: "bg-slate-400",
    label: "Superseded"
  }
} satisfies Record<
  AccountStatus | EditRequestStatus | SubmissionStatus | TemplateVersion["status"],
  {
    className: string;
    dot: string;
    label: string;
  }
>;

export function StatusPill({ status }: StatusPillProps) {
  const config = statusConfig[status];

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${config.className}`}
    >
      <span className={`h-2 w-2 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
}
